import React, { useState, useEffect } from "react";
import { useHistory } from "react-router";
import axios from "axios";
import { useSnackbar } from "notistack";
import PageWrapper from "../components/PageWrapper/PageWrapper";
import { FaAngleRight, FaPencilAlt, FaTimes } from "react-icons/fa";
import BackButton from "../components/BackButton";


const Account = () => {
  const [account, setAccount] = useState(null);
  const [editable, setEditable] = useState(false);
  const [name, setName] = useState("");
  const [originalName, setOriginalName] = useState("");
  const [currentPasscode, setCurrentPasscode] = useState("");
  const [newPasscode, setNewPasscode] = useState("");
  const [confirmPasscode, setConfirmPasscode] = useState("");
  const [passcodeEditable, setPasscodeEditable] = useState(false);
  const [hasPasscode, setHasPasscode] = useState(false);
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const auth = localStorage.getItem("authToken");
  const userId = localStorage.getItem("userId");

  useEffect(() => {
    if (userId){
      getAccount();
    } 
    else {
      history.push("/login");
    }
  }, []);

  const getAccount = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASEURL}/users/getuser`,
        {
          user_id: userId,
        },
        {
          headers: {
            Authorization: `Bearer ${auth}`,
          },
        }
      );
      if (response.data.success) {
        setAccount(response.data.data);
        setName(response.data.data.name);
        setOriginalName(response.data.data.name);
        setHasPasscode(!!response.data.data.has_passcode);
      }
    } catch (error) {
      console.error("Error occurred while fetching account details:", error);
    }
  };

  const handleUpdate = async () => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BASEURL}/users/updatedetails`,
        {
          user_id: userId,
          name: name,
          address: "A",
        },
        {
          headers: {
            Authorization: `Bearer ${auth}`,
          },
        }
      );
      if (response.data.success) {
        enqueueSnackbar("Name updated successfully", { variant: "success" });
        setEditable(false);
        setOriginalName(name);
      } else {
        enqueueSnackbar("Failed to update name", { variant: "error" });
      }
    } catch (error) {
      console.error("Error occurred while updating name:", error);
      enqueueSnackbar("Error occurred. Please try again.", {
        variant: "error",
      });
    }
  };

  const handlePasscodeUpdate = async () => {
    if (hasPasscode && (!currentPasscode || currentPasscode.length !== 6)) {
      enqueueSnackbar("Please enter your current 6-digit passcode", { variant: "error" });
      return;
    }
    if (!newPasscode || newPasscode.length !== 6 || !/^\d{6}$/.test(newPasscode)) {
      enqueueSnackbar("Please enter a valid 6-digit new passcode", { variant: "error" });
      return;
    }
    if (!confirmPasscode || confirmPasscode.length !== 6) {
      enqueueSnackbar("Please confirm your 6-digit passcode", { variant: "error" });
      return;
    }
    if (newPasscode !== confirmPasscode) {
      enqueueSnackbar("Passcode and confirm passcode do not match", { variant: "error" });
      return;
    }
    if (hasPasscode && currentPasscode === newPasscode) {
      enqueueSnackbar("New passcode cannot be the same as old passcode", { variant: "error" });
      return;
    }

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BASEURL}/users/updatepasscode`,
        {
          user_id: userId,
          current_passcode: hasPasscode ? currentPasscode : undefined,
          new_passcode: newPasscode,
          confirm_passcode: confirmPasscode,
        },
        {
          headers: {
            Authorization: `Bearer ${auth}`,
          },
        }
      );
      if (response.data.success) {
        enqueueSnackbar(response.data.message || "Passcode updated successfully", { variant: "success" });
        setPasscodeEditable(false);
        setCurrentPasscode("");
        setNewPasscode("");
        setConfirmPasscode("");
        setHasPasscode(true);
      } else {
        enqueueSnackbar(response.data.message || "Failed to update passcode", { variant: "error" });
      }
    } catch (error) {
      console.error("Error occurred while updating passcode:", error);
      enqueueSnackbar("Error occurred. Please try again.", { variant: "error" });
    }
  };

  const resetPasscodeForm = () => {
    setPasscodeEditable(false);
    setCurrentPasscode("");
    setNewPasscode("");
    setConfirmPasscode("");
  };

  const handleNameClick = () => {
    setEditable(true);
  };

  const handleCancel = () => {
    setEditable(false);
    setName(originalName);
  };


  return (
    <PageWrapper>
       <BackButton />
      <div className="py-2">
        <h1 className="text-2xl font-bold p-3">My Account</h1>
        {account && (
          <div className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center">
                <label className="block text-gray-700 text-sm font-bold mr-2">
                  Name:
                </label>
                {!editable && (
                  <p
                    className="text-gray-700 cursor-pointer"
                    onClick={handleNameClick}
                  >
                    {name}
                  </p>
                )}
              </div>

              {editable ? (
                <>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  />
                  <button
                    onClick={handleCancel}
                    className="ml-2"
                    title="Cancel"
                  >
                    <FaTimes />
                  </button>
                </>
              ) : (
                <div className="flex items-center">
                  <FaPencilAlt
                    className="ml-1 cursor-pointer"
                    onClick={handleNameClick}
                  />
                </div>
              )}
            </div>
            {editable && (
              <div className="flex items-center justify-end mb-4">
                <button
                  onClick={handleUpdate}
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                  type="button"
                >
                  Update
                </button>
              </div>
            )}
            <div className="flex items-center">
              <label className="block text-gray-700 text-sm font-bold mr-2">
                Phone No:
              </label>

              <div className="flex items-center">
                <p className="text-gray-700">{account.phone_no}</p>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <label className="block text-gray-700 text-sm font-bold mr-2">
                    6-Digit Passcode:
                  </label>
                  {!passcodeEditable && (
                    <p className="text-gray-700">
                      {hasPasscode ? "•••••• (Set)" : "Not Set"}
                    </p>
                  )}
                </div>

                {!passcodeEditable && (
                  <button
                    onClick={() => setPasscodeEditable(true)}
                    className="text-sm text-blue-600 hover:underline flex items-center gap-1 font-semibold"
                  >
                    <FaPencilAlt className="text-xs" />
                    {hasPasscode ? "Change Passcode" : "Set Passcode"}
                  </button>
                )}
              </div>

              {passcodeEditable && (
                <div className="mt-3 bg-gray-50 p-4 rounded-md border flex flex-col gap-3">
                  {hasPasscode && (
                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-semibold text-gray-600">Old / Current Passcode</label>
                      <input
                        type="password"
                        maxLength={6}
                        placeholder="Enter current 6 digits"
                        value={currentPasscode}
                        onChange={(e) => setCurrentPasscode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        className="shadow border rounded py-1.5 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline w-full max-w-xs"
                      />
                    </div>
                  )}

                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-semibold text-gray-600">
                      {hasPasscode ? "New Passcode" : "Passcode"}
                    </label>
                    <input
                      type="password"
                      maxLength={6}
                      placeholder="Enter 6 digits"
                      value={newPasscode}
                      onChange={(e) => setNewPasscode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      className="shadow border rounded py-1.5 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline w-full max-w-xs"
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-semibold text-gray-600">
                      {hasPasscode ? "Confirm New Passcode" : "Confirm Passcode"}
                    </label>
                    <input
                      type="password"
                      maxLength={6}
                      placeholder="Re-enter 6 digits"
                      value={confirmPasscode}
                      onChange={(e) => setConfirmPasscode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      className="shadow border rounded py-1.5 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline w-full max-w-xs"
                    />
                  </div>

                  <div className="flex items-center gap-2 mt-2">
                    <button
                      onClick={handlePasscodeUpdate}
                      className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1.5 px-4 text-sm rounded focus:outline-none focus:shadow-outline"
                      type="button"
                    >
                      {hasPasscode ? "Update Passcode" : "Set Passcode"}
                    </button>
                    <button
                      onClick={resetPasscodeForm}
                      className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-1.5 px-3 text-sm rounded focus:outline-none focus:shadow-outline"
                      type="button"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div
        className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4 flex items-center justify-between cursor-pointer"
        onClick={() => history.push("/orders")}
      >
        <p className="text-lg font-semibold">My Orders</p>

        <FaAngleRight className="text-gray-600 cursor-pointer hover:text-gray-900" />
      </div>
      <div
        className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4 flex items-center justify-between cursor-pointer"
        onClick={() => history.push("/my-address")}
      >
        <p className="text-lg font-semibold">My Address</p>

        <FaAngleRight className="text-gray-600 cursor-pointer hover:text-gray-900" />
      </div>
      <div
        className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4 flex items-center justify-between cursor-pointer"
        onClick={() =>{ localStorage.clear();history.push("/login")}}
      >
        <p className="text-lg font-semibold">Logout</p>

        <FaAngleRight className="text-gray-600 cursor-pointer hover:text-gray-900" />
      </div>
    </PageWrapper>
  );
};

export default Account;
